import { getApiAuthHeaders } from "../auth";

class ApiError extends Error {
  status: number;
  detail?: unknown;

  constructor(status: number, message: string, detail?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.detail = detail;
  }
}

async function request<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const headers = new Headers(init.headers);
  const authHeaders = getApiAuthHeaders();
  for (const [name, value] of Object.entries(authHeaders)) {
    headers.set(name, value);
  }
  if (init.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  const res = await fetch(path, { ...init, headers });
  const text = await res.text();
  let data: unknown;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = text;
  }
  if (!res.ok) {
    const detail =
      data && typeof data === "object" && "detail" in data
        ? (data as { detail: unknown }).detail
        : data;
    throw new ApiError(
      res.status,
      typeof detail === "string" ? detail : `HTTP ${res.status}`,
      detail,
    );
  }
  return data as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: "POST",
      body: body !== undefined ? JSON.stringify(body) : undefined,
    }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: "PUT",
      body: body !== undefined ? JSON.stringify(body) : undefined,
    }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: "PATCH",
      body: body !== undefined ? JSON.stringify(body) : undefined,
    }),
  del: <T>(path: string) => request<T>(path, { method: "DELETE" }),
  // Скачать xlsx через одноразовый grant-токен.
  //
  // Flow:
  //   1. POST /api/downloads/grant {resource, params} с init_data в заголовке
  //      → одноразовый URL `/api/downloads/<token>` (TTL 5 мин).
  //   2. Открыть этот URL способом, доступным в текущем окружении:
  //      a) Telegram.WebApp.downloadFile (Bot API 8.0+) — нативно,
  //         пользователь остаётся в mini-app, файл уходит в Files /
  //         Downloads. Лучший UX на iOS / macOS Desktop.
  //      b) Telegram.WebApp.openLink — открыть во внешнем браузере,
  //         тот скачает по Content-Disposition. Пользователь временно
  //         выходит из mini-app, но не теряет его (Telegram сохраняет).
  //      c) В обычном браузере — fetch + blob + <a download>.
  //
  // В отличие от старого `?init_data=…` подхода, токен в URL —
  // случайные 22 символа, одноразовый, без PII.
  async download(
    resource: string,
    params: Record<string, unknown> = {},
    fallbackName = "download.xlsx",
  ): Promise<void> {
    // 1) Запрашиваем grant.
    const grant = await api.post<{ url: string; expires_at: string }>(
      "/api/downloads/grant",
      { resource, params },
    );
    const absolute = new URL(grant.url, window.location.origin).toString();

    const tg = window.Telegram?.WebApp;

    // 2a) Native Telegram download (предпочтительно).
    if (tg && typeof tg.downloadFile === "function") {
      try {
        await new Promise<void>((resolve) => {
          tg.downloadFile!(
            { url: absolute, file_name: fallbackName },
            () => resolve(),
          );
          // Подстраховка — если callback не сработает за 30 сек, не виснем.
          setTimeout(resolve, 30_000);
        });
        return;
      } catch (_e) {
        // continue to openLink
      }
    }

    // 2b) openLink — внешний браузер.
    if (tg && typeof tg.openLink === "function") {
      try {
        tg.openLink(absolute);
        return;
      } catch (_e) {
        // continue to blob fallback
      }
    }

    // 2c) Обычный браузер.
    const res = await fetch(absolute);
    if (!res.ok) {
      throw new ApiError(res.status, `HTTP ${res.status}`);
    }
    const blob = await res.blob();
    const cd = res.headers.get("Content-Disposition") || "";
    const m = /filename="?([^"]+)"?/.exec(cd);
    const name = m?.[1] || fallbackName;
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
  },
};

export { ApiError };
